import { customStyle, defaultStyle } from '../../../../../../constant/custom-style';
import { setAmountWarning, setBulkData, updateRateAmountOfRate1865 } from '../../../../../../features/actuarial/rates-slice';
import { useDispatch, useSelector } from 'react-redux';
import { useProposalTypeData } from '../../../../../../hooks/proposal/use-proposal-type-data';
import HeaderRates from './header-rates';
import { RatesUpdateType } from '../../../../../../types/type';
import { AmountKind, getAmountKind, getRateAcronym1865, hasValidNumericFormat, isAllowedTextPrefix, isPlanComplete } from '../../../../../../helper/rates/rates-input-helper';
import BulkFillMonthsModal from '../bulk-fill-months-modal';
import InputFormatWarning from '../../../common/input-format-warning';
import InputFormatInfo from '../../../common/input-format-info';
import { useProposalComputation } from '../../../../../../hooks/proposal/use-proposal-computation';
import { useComputationActions } from '../../../../../../hooks/actions/user-computation-actions';
import { RootState } from '../../../../../../api/store';

// Centralized allowed special text values.
// Empty arrays mean no special text values are allowed; numeric values are still permitted.
const allowedPlanForGYRTorGPAFalse: string[] = ['N/A'];
const allowedPlanForGYRTorGPATrue: string[] = ['N/A'];
const allowedRiderForGYRTorGPAFalse: string[] = ['FREE', 'N/A'];
const allowedRiderForGYRTorGPATrue: string[] = ['FREE', 'N/A'];

export default function Rate1865() {

    const isLoadingComputeRates = useSelector((state: RootState) =>
        Object.values(state.api.mutations ?? {}).some(
            (mutation: any) => mutation?.endpointName === 'computeRates' && mutation?.status === 'pending' )
    );

    const dispatch = useDispatch();
    const { isRatesIsAgeType } = useProposalTypeData();
    const { 
        isUpdateRates, 
        rates1865: ratesOf1865,
        bulkValue, 
        bulkWarning, 
        isBulkFillOpen, 
        selectedMonths,
        amountWarning1865,
        is1865ActiveAgeBracket,
    } = useProposalComputation();
    const firstTheadTitle = isRatesIsAgeType ? 'Attained Age' : 'Term of Loan';
    const tableTitle = isRatesIsAgeType ? 'Enrolling Age 18-65' : 'Borrowers Age 18-65';

    const allowedStrings = ['N/A', 'FREE'];
    const availableMonths = Array.from(
        new Set((ratesOf1865 ?? []).map((group) => group.title).filter(Boolean))
    );

    const getAllowedValues = (type: RatesUpdateType): string[] => {
        if (isRatesIsAgeType) {
            return type === 'plan' ? allowedPlanForGYRTorGPATrue : allowedRiderForGYRTorGPATrue;
        }
        return type === 'plan' ? allowedPlanForGYRTorGPAFalse : allowedRiderForGYRTorGPAFalse;
    };

    const handleBulkFillSave1865 = () => {
        const trimmedValue = bulkValue.trim();
        const normalizedValue = trimmedValue.toUpperCase();
        const isAllowedString = allowedStrings.includes(normalizedValue);
        const isValidNumber =
            /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(trimmedValue) &&
            hasValidNumericFormat(trimmedValue);

        if (selectedMonths.length === 0) {
            dispatch(setBulkData({ bulkWarning: 'Please select at least one month.' }));
            return;
        }

        if (!trimmedValue || (!isAllowedString && !isValidNumber)) {
            dispatch(setBulkData({ bulkWarning: 'Enter N/A, FREE, or a valid number with up to 4 decimal places.' }));
            return;
        }

        const matchingGroups = (ratesOf1865 ?? []).filter((group) =>
            selectedMonths.includes(group.title)
        );

        if (matchingGroups.length === 0) {
            dispatch(setBulkData({ bulkWarning: 'No matching months found.' }));
            return;
        }

        matchingGroups.forEach((group) => {
            group.data.forEach((item) => {
                const id = item.type === 'plan' ? item.plan_id : item.rider_id;
                if (id === null || id === undefined) return;
                dispatch(updateRateAmountOfRate1865({
                    title: group.title,
                    id,
                    amount: isAllowedString ? normalizedValue : trimmedValue,
                    type: item.type,
                }));
            });
        });
        dispatch(setBulkData({ isBulkFillOpen: false }));
        dispatch(setBulkData({ selectedMonths: [] }));
        dispatch(setBulkData({ bulkValue: 'N/A' }));
        dispatch(setBulkData({ bulkWarning: null }));
        dispatch(setAmountWarning({ rateType: "rates1865", warning: null }))
    };

    




    const handleAmountChange = (title: string, id: number, value: string, type: RatesUpdateType) => {
        if(isLoadingComputeRates) return    
        dispatch(setAmountWarning({ rateType: 'rates1865', warning: null }));

        const acronym = getRateAcronym1865(id, type, ratesOf1865);
        const rateGroups = ratesOf1865 || [];
        const currentAgeGroup = rateGroups.find((group) => group.title === title);
        const newKind = getAmountKind(value);
        const normalizedValue = value.trim().toUpperCase();
        const isClearing = normalizedValue === '';
        const allowedValues = getAllowedValues(type);
        const isAgeBasedRider = isRatesIsAgeType && type === 'rider';
        const isSpecialRiderValue = ['FREE', 'N/A'].includes(normalizedValue);
        const isSpecialRiderPrefix = ['FREE', 'N/A'].some((allowed) => allowed.startsWith(normalizedValue));

        // Numeric values can have a maximum of 4 decimal places.
        if (newKind === 'number' && !isClearing && !hasValidNumericFormat(value)) {
            return;
        }

        // Locate the current plan or rider amount.
        const currentItem = currentAgeGroup?.data.find(
            (item) => item.type === type && (type === 'plan' ? item.plan_id === id : item.rider_id === id)
        );

        const currentAmount = currentItem?.amount?.trim().toUpperCase() ?? '';

        // Prevent adding characters after N/A is complete, but allow deleting it.
        if (type === 'plan' && currentAmount === 'N/A' && !isClearing && normalizedValue.startsWith(currentAmount)) {
            return;
        }

        if (isRatesIsAgeType && type === 'rider' && !isClearing) {
            const isValidRiderSpecialValue = ['FREE', 'N/A'].includes(normalizedValue);
            const isValidRiderNumber = newKind === 'number' && hasValidNumericFormat(value);
            if (isValidRiderSpecialValue || isValidRiderNumber) {
                dispatch(updateRateAmountOfRate1865({
                    title,
                    id,
                    amount: normalizedValue,
                    type: 'rider',
                }));
                return;
            }
        }

            // Require the previous month for the same plan to be completed first.
            // if (!isRatesIsAgeType && type === 'plan' && !isClearing) {
            //     const currentMonth = Number.parseInt(title, 10);

            //     if (currentMonth > 1) {
            //         const previousMonthTitle = `${currentMonth - 1} month${currentMonth - 1 === 1 ? '' : 's'}`;
            //         const previousMonthGroup = rateGroups.find(
            //             (group) => group.title === previousMonthTitle
            //         );

            //         const previousMonthPlan = previousMonthGroup?.data.find(
            //             (item) => item.type === 'plan' && item.plan_id === id
            //         );

            //         const previousMonthAmount = previousMonthPlan?.amount?.trim() ?? '';

            //         if (!previousMonthAmount) {
            //             dispatch(setAmountWarning({
            //                 rateType: 'rates1865',
            //                 warning: `Please enter the product amount for ${previousMonthTitle} before proceeding to ${title}.`,
            //             }));
            //             return;
            //         }
            //     }
            // }

            // Validate special text values using the centralized arrays.
            
        // If the existing value is FREE or N/A, ignore attempts to change it
        // except when clearing it or continuing to type a valid prefix.
        if (isAgeBasedRider && ['FREE', 'N/A'].includes(currentAmount) && !isClearing) {
            if (!isAllowedTextPrefix(normalizedValue, [currentAmount])) {
                return;
            }
        }

        // Validate special text values.
        if (newKind === 'text' && !isClearing) {
            if (isAgeBasedRider) {
                // F, FR, N, and N/ are valid prefixes; FRO and N? are invalid.
                if (!isSpecialRiderPrefix && !isSpecialRiderValue) {
                    dispatch(setAmountWarning({
                        rateType: 'rates1865',
                        warning: 'Rider amounts must be numeric, FREE, or N/A.',
                    }));
                    return;
                }
            } else {
                const isAllowedText = allowedValues.some((allowed) => allowed.startsWith(normalizedValue));
                if (!isAllowedText) {
                    dispatch(setAmountWarning({
                        rateType: 'rates1865',
                        warning: type === 'plan'
                            ? 'Product amounts must be numeric with a maximum of 4 decimal places or N/A.'
                            : 'Rider amounts must be numeric, FREE, or N/A.',
                    }));
                    return;
                }
            }
        }


        // Preserve the existing special-value editing behavior for riders.
        if (type === 'rider' && ['FREE', 'N/A'].includes(currentAmount) && !isClearing) {
            if (!isAllowedTextPrefix(normalizedValue, [currentAmount])) {
                return;
            }
        }

        // If a legacy N/A plan is cleared in the GYRT/GPA flow,
        // clear its same-age-group N/A riders as well.
        if (isRatesIsAgeType && type === 'plan' && currentAmount === 'N/A' && isClearing) {
            dispatch(updateRateAmountOfRate1865({
                title,
                id,
                amount: '',
                type: 'plan',
            }));

            currentAgeGroup?.data
                .filter((item) =>
                    item.type === 'rider' &&
                    item.rider_id != null &&
                    item.amount?.trim().toUpperCase() === 'N/A'
                )
                .forEach((rider) => {
                    dispatch(updateRateAmountOfRate1865({
                        title,
                        id: rider.rider_id!,
                        amount: '',
                        type: 'rider',
                    }));
                });
                return;
            }

            const acceptedValue = isClearing ? '' : normalizedValue;

            // First-month FREE/N/A riders populate the same rider in subsequent months.
            const shouldAutoPopulateRider = !isRatesIsAgeType && type === 'rider' && ['FREE', 'N/A'].includes(acceptedValue);

            // Preserve value-type consistency across months.
            const otherAmounts = !isRatesIsAgeType
                ? rateGroups
                    .filter((group) => group.title !== title)
                    .map((group) => ({
                        title: group.title,
                        amount: group.data.find((item) => {
                            if (item.type !== type) return false;
                            return type === 'plan'
                                ? item.plan_id === id
                                : item.rider_id === id;
                        })?.amount?.trim() ?? '',
                    }))
                    .filter((item) => item.amount !== '')
                : [];

            if (!isRatesIsAgeType && type === 'rider' && newKind !== null && !isClearing && !shouldAutoPopulateRider) {
                const inconsistentAmount = otherAmounts.find((item) => getAmountKind(item.amount) !== newKind);

                if (inconsistentAmount) {
                    const existingKind = getAmountKind(inconsistentAmount.amount);
                    dispatch(setAmountWarning({
                        rateType: 'rates1865',
                        warning: `${acronym} must remain ${existingKind === 'number' ? 'numeric' : 'text'} across the loan terms.`,
                    }));
                    return;
                }
            }

            // Clear a legacy special value across all months when it is removed.
            const shouldClearAllMonths = !isRatesIsAgeType && isClearing && ((type === 'plan' && currentAmount === 'N/A') || (type === 'rider' && ['FREE', 'N/A'].includes(currentAmount)));

            if (shouldClearAllMonths) {
                rateGroups.forEach((group) => {
                    const matchingItem = group.data.find((item) => {
                        if (type === 'plan') {
                            return item.type === 'plan' && item.plan_id === id;
                        }
                        return item.type === 'rider' && item.rider_id === id;
                    });

                    if (!matchingItem) return;

                    dispatch(updateRateAmountOfRate1865({
                        title: group.title,
                        id,
                        amount: '',
                        type,
                    }));
                });
                return;
            }

            // Update the current amount.
            dispatch(updateRateAmountOfRate1865({
                title,
                id,
                amount: acceptedValue,
                type,
            }));

            // Auto-populate first-month FREE/N/A rider values into subsequent months.
            // if (shouldAutoPopulateRider) {
            //     rateGroups
            //         .filter((group) => Number.parseInt(group.title, 10) > 1)
            //         .forEach((group) => {
            //             const matchingRider = group.data.find(
            //                 (item) => item.type === 'rider' && item.rider_id === id
            //             );

            //             if (!matchingRider) return;

            //             dispatch(updateRateAmountOfRate1865({
            //                 title: group.title,
            //                 id,
            //                 amount: acceptedValue,
            //                 type: 'rider',
            //             }));
            //         });
            // }
            // Auto-populate FREE/N/A rider values across all months, regardless of the edited month.
            if (shouldAutoPopulateRider) {
                rateGroups.forEach((group) => {
                    const matchingRider = group.data.find(
                        (item) => item.type === 'rider' && item.rider_id === id
                    );

                    if (!matchingRider) return;

                    dispatch(updateRateAmountOfRate1865({
                        title: group.title,
                        id,
                        amount: acceptedValue,
                        type: 'rider',
                    }));
                });
            }

            // Preserve value-type consistency across ages for GYRT/GPA.
            if (isRatesIsAgeType) {
                const otherAmounts = rateGroups
                    .filter((group) => group.title !== title)
                    .map((group) =>
                        group.data.find((item) => {
                            if (item.type !== type) return false;
                            return type === 'plan'
                                ? item.plan_id === id
                                : item.rider_id === id;
                        })?.amount
                    )
                    .filter(
                        (amount): amount is string =>
                            typeof amount === 'string' && amount.trim() !== ''
                    );

                const existingKinds = new Set(
                    otherAmounts
                        .map(getAmountKind)
                        .filter((kind): kind is AmountKind => kind !== null)
                );

                if (existingKinds.size > 1) {
                    dispatch(updateRateAmountOfRate1865({
                        title,
                        id,
                        amount: currentAmount,
                        type,
                    }));

                    dispatch(setAmountWarning({
                        rateType: 'rates1865',
                        warning: `${acronym} already contains mixed value types across ages 18–65. Please correct the inconsistent values before editing.`,
                    }));
                    return;
                }

                const existingKind = Array.from(existingKinds)[0] as AmountKind | undefined;

                if (newKind !== null && existingKind && newKind !== existingKind) {
                    dispatch(updateRateAmountOfRate1865({
                        title,
                        id,
                        amount: currentAmount,
                        type,
                    }));

                    dispatch(setAmountWarning({
                        rateType: 'rates1865',
                        warning: `${acronym} must remain ${existingKind === 'number' ? 'numeric' : 'text'} across ages 18–65. Your change was not applied.`,
                    }));
                    return;
                }
            }
        };





    return (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="flex min-h-[42px] items-center justify-between gap-2 border-b border-gray-200 bg-[#186cb2]/10 px-3 py-2.5">
                <HeaderRates title={tableTitle} fontSize="text-xs font-semibold text-gray-700" fontWeight="" />
                <div className="flex items-center gap-2">
                    {/* {!isRatesIsAgeType && isUpdateRates && (
                        <button
                            type="button"
                            onClick={() => {
                                dispatch(setBulkData({
                                    bulkWarning: null,
                                    isBulkFillOpen: true,
                                    activeAgeBracket: "18-65",
                                }));
                            }}
                            className="rounded-md border border-[#186cb2]/30 bg-white px-2.5 py-1 text-[10px] font-semibold text-[#186cb2] transition-colors hover:bg-[#186cb2]/10"
                        >
                            Bulk Fill
                        </button>
                    )} */}
                    {isUpdateRates && (
                        <span className="rounded-md border border-[#186cb2]/20 bg-[#186cb2]/10 px-2.5 py-1 text-[10px] font-medium text-[#186cb2]">
                            Editing
                        </span>
                    )}
                </div>
            </div>
            <InputFormatInfo isShow={isUpdateRates} isNoFree={false}/>
            {isUpdateRates && amountWarning1865 && (
                <InputFormatWarning
                    warningMessage={amountWarning1865}
                    warningClose={() => dispatch(setAmountWarning({ rateType: "rates1865", warning: null }))}
                />
            )}

            <div className="overflow-x-auto">
                <table className="w-full min-w-max border-collapse">
                    <thead>
                        <tr className="">
                            <th className="sticky left-0 z-10 border-b border-r border-gray-200 bg-gray-50 px-3 py-2 text-center text-[11px] font-semibold text-gray-600">
                                {firstTheadTitle}
                            </th>
                            {ratesOf1865?.[0]?.data.map((item, index, array) => (
                                <th key={index} className={`border-b border-r border-gray-200 px-3 py-2 text-center text-[11px] font-semibold text-gray-600 ${index === array.length - 1 ? 'border-r-0' : ''}`}>
                                    {item.acronym}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {ratesOf1865?.map((group, groupIndex) => (
                            <tr key={group.title} className={`border-b border-gray-100 last:border-b-0 ${groupIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} hover:bg-[#186cb2]/5`}>
                                <td className={`sticky left-0 z-10 border-r border-gray-200 px-3 py-1.5 text-center text-[11px] font-medium text-gray-700 ${groupIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                                    {group.title}
                                </td>
                                {group.data.map((item, index, array) => {
                                    const id = item.plan_id !== null ? item.plan_id : item.rider_id;
                                    if (id === null || id === undefined) return null;

                                    return (
                                        <td key={index} className={`px-2 py-1.5 ${index !== array.length - 1 ? 'border-r border-gray-100' : ''}`}>
                                            <input
                                                disabled={!isUpdateRates || isLoadingComputeRates}
                                                type="text"
                                                value={item.amount}
                                                onChange={(e) => handleAmountChange(group.title, id, e.target.value.toUpperCase(), item.type)}
                                                className={`w-full min-w-[80px] rounded border px-2 py-1 text-right text-[11px] uppercase outline-none transition-colors ${defaultStyle} ${isUpdateRates ? item.amount ? customStyle : 'border-gray-300 bg-white focus:border-[#186cb2] focus:ring-1 focus:ring-[#186cb2]/20' : 'border-transparent bg-transparent'}`}
                                            />
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {isBulkFillOpen && is1865ActiveAgeBracket && !isRatesIsAgeType && isUpdateRates && (
                <BulkFillMonthsModal
                    typeOfAgeBracket="16-65"
                    availableMonths={availableMonths}
                    bulkValue={bulkValue}
                    setBulkValue={(value) => dispatch(setBulkData({bulkValue: value}))}
                    selectedMonths={selectedMonths}
                    setSelectedMonths={(value) => dispatch(setBulkData({ selectedMonths: value }))}
                    bulkWarning={bulkWarning}
                    setBulkWarning={(value) => dispatch(setBulkData({ bulkWarning: value }))}
                    isBulkFillOpen={isBulkFillOpen}
                    setIsBulkFillOpen={(value) => dispatch(setBulkData({ isBulkFillOpen: value }))}
                    handleSaveBulk={handleBulkFillSave1865}
                />
            )}
        </div>
    );
}